import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ChevronDownIcon, Mail } from 'lucide-react';
type FAQItem = {
  question: string;
  answer: string;
};
export function FAQSection() {
  const [openIndex, setOpenIndex] = useState<number | null>(null);
  const faqs: FAQItem[] = [{
    question: '¿Qué aprenderé exactamente en este recetario?',
    answer: 'Aprenderás técnicas milenarias de cocina: desde la creación de masa madre y fermentos naturales, hasta caldos reconstituyentes, uso de hierbas medicinales y conservación natural de alimentos.'
  }, {
    question: '¿Necesito ingredientes difíciles de conseguir?',
    answer: 'No. Nos enfocamos en ingredientes reales que encuentras en ferias locales, dietéticas o incluso en tu propio jardín. Volvemos a lo simple y natural.'
  }, {
    question: '¿Es apto para personas con intolerancias?',
    answer: '¡Sí! Muchas de las técnicas ancestrales (como la fermentación larga de granos) ayudan a que los alimentos sean mucho más fáciles de digerir, incluso para personas con sensibilidades.'
  }, {
    question: '¿Cómo recibo el material tras la compra?',
    answer: 'El acceso es inmediato. Una vez confirmado el pago, recibirás un email con los enlaces de descarga de todos los PDFs para que empieces a cocinar hoy mismo.'
  }, {
    question: '¿Puedo leerlo desde mi celular?',
    answer: 'Totalmente. Todos los manuales están optimizados para verse perfectamente en smartphones, tablets y computadoras.'
  }, {
    question: '¿Qué pasa si tengo una duda con una receta?',
    answer: 'Contamos con un canal de soporte vía email donde respondemos todas tus consultas técnicas sobre las preparaciones del libro.'
  }, {
    question: '¿Ofrecen garantía de satisfacción?',
    answer: 'Sí, tienes 7 días de garantía total. Si el contenido no es lo que esperabas, te devolvemos el 100% de tu dinero sin preguntas.'
  }, {
    question: '¿Es una estafa? ¿Cómo sé que recibiré el material?',
    answer: 'Entendemos tu duda. Somos una empresa real con miles de alumnas satisfechas. El pago es procesado por Mercado Pago (100% seguro) y el envío es automático a tu email.'
  }];
  const toggleFAQ = (index: number) => {
    setOpenIndex(openIndex === index ? null : index);
  };
  return <section id="faq" className="py-20 bg-transparent relative z-10 pb-[45px] space-y-[10px] pt-[4px]" data-id="element-111">
      <div className="max-w-4xl mx-auto px-4" data-id="element-112">
        <div className="text-center mb-16" data-id="element-113">
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-white border border-[#0f1711]/10 text-[#3e2723] text-sm font-medium mb-3" data-id="element-114">
            <span className="flex h-2 w-2 rounded-full bg-[#8b7355]" data-id="element-115"></span>
            ¿Tenés dudas?
          </motion.div>

          <motion.h2 initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} className="text-4xl md:text-5xl font-extrabold mb-2 tracking-tight text-[#0f1711]" data-id="element-116">
            Preguntas Frecuentes
          </motion.h2>
          <motion.p initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          delay: 0.1
        }} className="text-lg text-[#3e2723]/70" data-id="element-117">
            Todo lo que necesitás saber antes de empezar
          </motion.p>
        </div>

        <div className="space-y-4" data-id="element-118">
          {faqs.map((faq, index) => <motion.div key={index} initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          delay: index * 0.05
        }} className="bg-white border border-[#0f1711]/10 rounded-2xl overflow-hidden hover:border-[#8b7355]/30 transition-colors shadow-sm" data-id="element-119">
              <button onClick={() => toggleFAQ(index)} aria-expanded={openIndex === index} className="w-full flex items-center justify-between p-4 text-left focus:outline-none group" data-id="element-120">
                <span className="text-lg font-bold text-[#0f1711] pr-8 group-hover:text-[#8b7355] transition-colors" data-id="element-121">
                  {faq.question}
                </span>
                <motion.div animate={{
              rotate: openIndex === index ? 180 : 0
            }} transition={{
              duration: 0.3
            }} className="shrink-0" data-id="element-122">
                  <ChevronDownIcon className="w-5 h-5 text-[#3e2723]/50 group-hover:text-[#8b7355] transition-colors" data-id="element-123" />
                </motion.div>
              </button>

              <AnimatePresence data-id="element-124">
                {openIndex === index && <motion.div initial={{
              height: 0,
              opacity: 0
            }} animate={{
              height: 'auto',
              opacity: 1
            }} exit={{
              height: 0,
              opacity: 0
            }} transition={{
              duration: 0.3
            }} className="overflow-hidden" data-id="element-125">
                    <div className="px-4 pb-4 text-[#3e2723]/80 leading-relaxed border-t border-[#0f1711]/5 pt-3" data-id="element-126">
                      {faq.answer}
                    </div>
                  </motion.div>}
              </AnimatePresence>
            </motion.div>)}
        </div>

        {/* Support CTA */}
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        delay: 0.3
      }} className="mt-16 p-8 md:p-10 bg-[#8b7355]/5 border border-[#8b7355]/20 rounded-[2rem] text-center shadow-sm relative overflow-hidden flex flex-col items-center" data-id="element-127">
          <div className="absolute -top-10 -right-10 w-32 h-32 bg-[#8b7355]/10 rounded-full blur-2xl" data-id="element-128" />

          <p className="text-[#3e2723] text-xl font-bold mb-6 relative z-10" data-id="element-129">
            ¿Todavía tenés dudas o problemas con tu compra?
          </p>

          <a href="mailto:soportedigitalfactory@gmail.com" className="inline-flex gap-3 text-white font-black px-8 py-4 rounded-2xl bg-[#B1926D] hover:bg-[#7a6344] transition-all hover:scale-105 shadow-lg relative z-10 group items-end justify-start" data-id="element-130">
            <Mail className="w-5 h-5 group-hover:rotate-12 transition-transform" data-id="element-131" />
            <span className="text-[15px] pt-[0px] pb-[0px]" data-id="element-132">
              Contactanos: soportedigitalfactory@gmail.com
            </span>
          </a>
        </motion.div>
      </div>
    </section>;
}